import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BarChart3, Coffee, Phone, Play, Settings, Square, Voicemail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DialerKeypad from "@/components/dialer/DialerKeypad";
import SettingsTab from "@/components/dialer/SettingsTab";
import PowerDialerRail from "@/components/dialer/power-dialer/PowerDialerRail";
import { PowerDialerCallPane } from "@/components/dialer/power-dialer/PowerDialerCallPane";
import { WrapUpDialog } from "@/components/dialer/power-dialer/WrapUpDialog";
import { PreviewModePane } from "@/components/dialer/power-dialer/PreviewModePane";
import { SessionSummaryDialog } from "@/components/dialer/power-dialer/SessionSummaryDialog";
import { ShortcutsHelpDialog } from "@/components/dialer/power-dialer/ShortcutsHelpDialog";
import { ReturnCallBanner } from "@/components/dialer/power-dialer/ReturnCallBanner";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { usePowerDialer } from "@/context/PowerDialerContext";
import { AddOnType } from "@/enum/AddOnType";
import { useAddOn } from "@/hooks/useAddOn";
import { MAX_LINES, emptyQueueMessage, formatAbandonRate } from "@/util/dialer";
import { MachineDetectionMode } from "@/enum/MachineDetectionMode";
import { DialerQueueGroup } from "@/enum/DialerQueueGroup";
import { useGetDialerQueueCountsQuery, useGetHomeQuery, useGetOwnPhoneNumbersQuery } from "@/store/rest_api";
import { DialerSeat } from "@/enum/DialerSeat";
import type { DialerSessionStatus } from "@/model/interface/DialerSession";
import TelemarketingComplianceDialog from "@/components/pages/power-dialer/TelemarketingComplianceDialog";
import { cn } from "@/lib/utils";

const SESSION_STATUS_LABELS: Record<DialerSessionStatus, string> = {
  READY: "Ready",
  DIALING: "Dialing",
  CONNECTED: "On a call",
  WRAP_UP: "Wrap-up",
  BREAK: "On break",
  ENDED: "Ended",
};

export default function PowerDialerLayout() {
  const navigate = useNavigate();
  const {
    campaign,
    settings,
    endSession,
    isEndingAfterCall,
    wrapUpCall,
    isDialing,
    isPreview,
    startDialing,
    stopDialing,
    lines,
    updateLines,
    isUpdatingLines,
    updateCallerId,
    stopReason,
    sessionId,
    activeCall,
    roomCall,
    isLineOpen,
    lineCalls,
    sessionStatus,
    markReady,
    takeBreak,
    isSessionActionPending,
  } = usePowerDialer();
  const { data: queueCounts } = useGetDialerQueueCountsQuery(campaign?.id ?? "", { skip: !campaign?.id });
  const totalDialed = queueCounts?.dialed ?? 0;
  const totalAnswered = queueCounts?.answered ?? 0;
  const totalVoicemail = queueCounts?.voicemail ?? 0;
  const totalFailed = queueCounts?.failed ?? 0;
  const pacedLines = queueCounts?.pacedLines ?? null;

  const { data: user } = useGetHomeQuery();
  const { data: phoneNumbers = [] } = useGetOwnPhoneNumbersQuery();
  const hasMultiLine = useAddOn(AddOnType.POWER_DIALER_MULTI);
  const lineOptions = hasMultiLine ? Array.from({ length: MAX_LINES }, (_, index) => index + 1) : [1];
  const hasSingleLineSeat = user?.activeMembership?.dialerSeat === DialerSeat.SINGLE_LINE;

  const [isStopConfirmOpen, setIsStopConfirmOpen] = useState(false);
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);
  const [isComplianceOpen, setIsComplianceOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [isAudioSettingsOpen, setIsAudioSettingsOpen] = useState(false);
  const [isKeypadOpen, setIsKeypadOpen] = useState(false);
  const [keypadValue, setKeypadValue] = useState("");
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);
  useKeyboardShortcuts({
    b: () => handleBreakToggle(),
    "?": () => setIsShortcutsOpen(true),
  });

  const leaveToDashboard = () => navigate("/dashboard");

  const handleCallerIdChange = (phoneNumberId: string) => {
    const chosen = phoneNumbers.find((number) => number.id === phoneNumberId);
    if (chosen) {
      void updateCallerId(chosen);
    }
  };

  const handleBackToDashboard = () => {
    if (activeCall || wrapUpCall) {
      setIsLeaveConfirmOpen(true);
      return;
    }
    void endSession({ afterCurrentCall: false, onEnded: leaveToDashboard });
  };

  const handleEndAfterCall = () => {
    setIsLeaveConfirmOpen(false);
    void endSession({ afterCurrentCall: true, onEnded: leaveToDashboard });
  };

  const handleLeaveNow = () => {
    setIsLeaveConfirmOpen(false);
    void endSession({ afterCurrentCall: false, onEnded: leaveToDashboard });
  };

  const handleKeypadPressed = () => {
    setKeypadValue("");
    setIsKeypadOpen(true);
  };

  const handleKeypadChange = (value: string) => {
    if (value.startsWith(keypadValue)) {
      const appended = value.slice(keypadValue.length);
      if (appended && roomCall) {
        roomCall.sendDigits(appended);
      }
    }
    setKeypadValue(value);
  };

  const handleStopRequested = () => {
    if (activeCall || lineCalls.length > 0) {
      setIsStopConfirmOpen(true);
    } else {
      handleStopConfirmed();
    }
  };

  const handleBreakToggle = () => {
    if (isSessionActionPending) {
      return;
    }
    if (isOnBreak) {
      void markReady();
      return;
    }
    if (canTakeBreak) {
      void takeBreak();
    }
  };

  const handleStopConfirmed = async () => {
    setIsStopConfirmOpen(false);
    await stopDialing();
    setIsSummaryOpen(true);
  };

  const autoVoicemailOn = Boolean(
    settings?.machineDetectionMode === MachineDetectionMode.DETECT_FIRST &&
      (settings?.voicemailMessageFileId || settings?.voicemailDropUrl),
  );
  const isLineConnecting = Boolean(sessionId) && !isLineOpen;
  const isOnBreak = sessionStatus === "BREAK";
  const isWaitingForReady = sessionStatus === "WRAP_UP" && !wrapUpCall;
  const canTakeBreak =
    isDialing && (sessionStatus === "READY" || sessionStatus === "DIALING" || isWaitingForReady);
  const isQueueEmpty =
    isDialing && lineCalls.length === 0 && queueCounts !== undefined && !queueCounts.groups[DialerQueueGroup.DUE_NOW];

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background text-foreground">
      <div className="flex shrink-0 items-center gap-3 border-b border-border px-6 py-3.5">
        <span className="flex items-center gap-2.5 text-lg font-bold dark:text-white">
          <span
            className={cn(
              "h-2.5 w-2.5 rounded-full",
              isDialing ? "animate-pulse bg-success" : "bg-muted-foreground/40",
            )}
          />
          Power Dialer
          {isPreview && <span className="text-base font-semibold text-muted-foreground">· Preview mode</span>}
          {campaign?.name && (
            <span className="text-base font-semibold text-muted-foreground">· {campaign.name}</span>
          )}
        </span>

        <div className="ml-auto flex items-center gap-2">
          {isLineConnecting && <span className="text-sm text-muted-foreground">Connecting your line</span>}
          {!isDialing && stopReason && (
            <span className="text-sm text-destructive">{stopReason}</span>
          )}

          {sessionStatus && sessionStatus !== "ENDED" && (
            <span
              className={cn(
                "status-pill px-3 py-1 text-sm",
                isOnBreak ? "status-pill-warning" : "status-pill-neutral",
              )}
            >
              {SESSION_STATUS_LABELS[sessionStatus]}
            </span>
          )}
          {isOnBreak && (
            <Button variant="outline" onClick={() => void markReady()} disabled={isSessionActionPending}>
              <Play className="mr-2 h-4 w-4" />
              Resume
            </Button>
          )}
          {isWaitingForReady && (
            <Button variant="outline" onClick={() => void markReady()} disabled={isSessionActionPending}>
              Ready
            </Button>
          )}
          {canTakeBreak && (
            <Button variant="outline" onClick={() => void takeBreak()} disabled={isSessionActionPending}>
              <Coffee className="mr-2 h-4 w-4" />
              Break
            </Button>
          )}

          {!isDialing ? (
            <Button onClick={() => setIsComplianceOpen(true)}>
              <Play className="mr-2 h-4 w-4" />
              Start
            </Button>
          ) : (
            <Button
              variant="outline"
              className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={handleStopRequested}
            >
              <Square className="mr-2 h-4 w-4" />
              Stop
            </Button>
          )}

          <Button size="icon" variant="ghost" onClick={() => setIsSummaryOpen(true)} title="Session summary">
            <BarChart3 className="h-5 w-5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setIsAudioSettingsOpen(true)}
            title="Audio settings"
          >
            <Settings className="h-5 w-5" />
          </Button>
          {isEndingAfterCall && <span className="text-sm text-muted-foreground">Ending after this call</span>}
          <Button variant="ghost" onClick={handleBackToDashboard} disabled={isEndingAfterCall}>
            <ArrowLeft className="mr-2 h-5 w-5" />
            Dashboard
          </Button>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2.5 border-b border-border bg-muted/40 px-6 py-2.5">
        <span className="status-pill status-pill-neutral px-3 py-1 text-sm tabular-nums">Dialed {totalDialed}</span>
        <span className="status-pill status-pill-success px-3 py-1 text-sm tabular-nums">
          Answered {totalAnswered}
        </span>
        <span className="status-pill status-pill-warning px-3 py-1 text-sm tabular-nums">
          Voicemail {totalVoicemail}
        </span>
        <span className="status-pill status-pill-destructive px-3 py-1 text-sm tabular-nums">
          Failed {totalFailed}
        </span>
        {queueCounts && (
          <span
            className={cn(
              "status-pill px-3 py-1 text-sm tabular-nums",
              pacedLines ? "status-pill-warning" : "status-pill-neutral",
            )}
            title="Abandoned calls over calls answered by a person in the last 30 days"
          >
            Abandoned {formatAbandonRate(queueCounts.abandonRate)}
          </span>
        )}

        <div className="ml-auto flex items-center gap-3">
          {pacedLines && !isPreview && (
            <span className="text-sm text-muted-foreground">
              Paced to {pacedLines} {pacedLines === 1 ? "line" : "lines"} until abandoned calls fall under 1.5% over 30 days
            </span>
          )}
          {autoVoicemailOn && (
            <span className="status-pill status-pill-neutral gap-1.5 px-3 py-1 text-sm">
              <Voicemail className="h-4 w-4" />
              Auto voicemail on
            </span>
          )}

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Caller ID</span>
            <Select
              value={campaign?.phoneNumber?.id ?? ""}
              onValueChange={handleCallerIdChange}
              disabled={isDialing || isUpdatingLines}
            >
              <SelectTrigger className="h-9 w-52 text-sm">
                <SelectValue placeholder="Choose a caller ID" />
              </SelectTrigger>
              <SelectContent>
                {phoneNumbers.map((number) => (
                  <SelectItem key={number.id} value={number.id!}>
                    {number.alias ? `${number.alias} (${number.number})` : number.number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!isPreview && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Lines</span>
              <Select
                value={String(lines)}
                onValueChange={(value) => updateLines(Number(value))}
                disabled={isDialing || isUpdatingLines || !hasMultiLine}
              >
                <SelectTrigger className="h-9 w-18 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {lineOptions.map((count) => (
                    <SelectItem key={count} value={String(count)}>
                      {count}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!hasMultiLine && (
                <span className="text-sm text-muted-foreground">
                  Multi line dialing needs the multi line add-on
                </span>
              )}
              {hasMultiLine && hasSingleLineSeat && lines > 1 && (
                <span className="text-sm text-muted-foreground">Your single-line seat dials one line</span>
              )}
            </div>
          )}
        </div>
      </div>

      <ReturnCallBanner />

      <div className="flex min-h-0 flex-1">
        <PowerDialerRail />

        <div className="flex min-w-0 flex-1 flex-col">
          {activeCall ? (
            <PowerDialerCallPane onKeypadPressed={handleKeypadPressed} areShortcutsPaused={isKeypadOpen} />
          ) : isPreview ? (
            <div className="mx-auto w-full max-w-md pt-10">
              <PreviewModePane />
              {!isDialing && (
                <p className="mt-6 text-center text-base text-muted-foreground">
                  Start the session to begin previewing contacts.
                </p>
              )}
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <Phone className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-lg text-muted-foreground">{isOnBreak ? "You're on a break" : "No active call"}</p>
              <p className="mt-1.5 text-base text-muted-foreground">
                {isLineConnecting
                  ? "Connecting your line"
                  : isOnBreak
                    ? "Resume when you're ready to take calls."
                    : isWaitingForReady
                      ? "Choose Ready to dial the next lead."
                      : isQueueEmpty
                        ? emptyQueueMessage(queueCounts?.nextDueAt)
                        : isDialing
                          ? "Dialing contacts. You're connected the moment someone answers."
                          : "Start the power dialer to begin"}
              </p>
            </div>
          )}
        </div>
      </div>

      <WrapUpDialog />
      <ShortcutsHelpDialog open={isShortcutsOpen} onOpenChange={setIsShortcutsOpen} />
      <SessionSummaryDialog open={isSummaryOpen} onClose={() => setIsSummaryOpen(false)} />

      <TelemarketingComplianceDialog
        open={isComplianceOpen}
        campaignId={campaign?.id ?? null}
        lines={isPreview ? 1 : lines}
        onOpenChange={setIsComplianceOpen}
        onConfirm={() => {
          setIsComplianceOpen(false);
          startDialing(true);
        }}
      />

      <AlertDialog open={isStopConfirmOpen} onOpenChange={setIsStopConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stop dialing?</AlertDialogTitle>
            <AlertDialogDescription>
              {activeCall
                ? "Your conversation stays connected. Calls that are still ringing go back into the queue."
                : "Calls that are still ringing go back into the queue."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep dialing</AlertDialogCancel>
            <AlertDialogAction onClick={handleStopConfirmed}>Stop</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isLeaveConfirmOpen} onOpenChange={setIsLeaveConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>You're on a call</AlertDialogTitle>
            <AlertDialogDescription>
              End after this call keeps the conversation and its wrap-up, then leaves. Leave now hangs up on the lead, who is called back first when you return.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Stay</AlertDialogCancel>
            <Button variant="outline" onClick={handleLeaveNow}>
              Leave now
            </Button>
            <AlertDialogAction onClick={handleEndAfterCall}>End after this call</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isAudioSettingsOpen} onOpenChange={setIsAudioSettingsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Audio settings</DialogTitle>
          </DialogHeader>
          <SettingsTab />
        </DialogContent>
      </Dialog>

      <Dialog open={isKeypadOpen} onOpenChange={setIsKeypadOpen}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle>Keypad</DialogTitle>
          </DialogHeader>
          <DialerKeypad
            value={keypadValue}
            onChange={handleKeypadChange}
            onCall={() => setIsKeypadOpen(false)}
            inCallMode
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
